import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  ExternalLink, 
  Tag, 
  ShoppingBag, 
  Copy,
  Check,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MarketplacePage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/marketplace/brands`);
        setBrands(res.data.brands || []);
      } catch (err) {
        console.error('Failed to fetch brands:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success(`Code "${code}" copied!`);
    setTimeout(() => setCopiedCode(null), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding">
          <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Support Black-Owned</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="marketplace-title">
            The Marketplace
          </h1>
          <p className="text-cream/70 font-body max-w-2xl">
            Discover and support minority-owned golf brands. Exclusive discount codes for Fairway Alliance members.
          </p>
        </div>
      </div>

      <div className="container-padding">
        {/* Member Discount Banner */}
        <div className="mb-8 p-6 bg-gradient-to-r from-fairway to-deep-green rounded-sm text-white">
          <div className="flex items-center gap-3 mb-2">
            <Tag className="w-5 h-5 text-gold" />
            <h2 className="font-display font-semibold text-lg">Member Exclusive Discounts</h2>
          </div>
          <p className="text-cream/80 text-sm font-body">
            Birdie and Eagle members get exclusive promo codes from every brand below. 
            Show your digital Member Card at partner courses and shops for in-store discounts.
          </p>
        </div>

        {/* Brands Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.map((brand) => (
            <Card key={brand.brand_id || brand.id} className="bg-white border-0 shadow-sm hover:shadow-lg transition-all group overflow-hidden" data-testid="marketplace-brand-card">
              {/* Brand Image */}
              <div className="relative h-48 bg-fairway/5 overflow-hidden">
                <img 
                  src={brand.image_url} 
                  alt={brand.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {brand.featured && (
                  <Badge className="absolute top-3 right-3 bg-gold text-deep-green font-semibold">Featured</Badge>
                )}
              </div>

              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-display font-bold text-xl text-deep-green">{brand.name}</h3>
                  {brand.minority_owned && (
                    <Badge className="bg-gold/20 text-gold text-xs">Black-Owned</Badge>
                  )}
                </div>
                <p className="text-gold font-semibold text-sm mb-3 italic">{brand.tagline}</p>
                <p className="text-muted-foreground text-sm font-body mb-4 line-clamp-3">
                  {brand.description}
                </p>

                {/* Categories */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {brand.categories?.map((cat) => (
                    <Badge key={cat} variant="outline" className="text-xs border-gray-200">{cat}</Badge>
                  ))}
                </div>

                {/* Promo Code */}
                {brand.promo_code && (
                  <div className="bg-bone p-3 rounded-sm mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">{brand.promo_description}</p>
                        <p className="font-mono font-bold text-fairway text-lg tracking-wider">{brand.promo_code}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyCode(brand.promo_code)}
                        className="border-fairway text-fairway hover:bg-fairway hover:text-white"
                        data-testid="copy-promo-code"
                      >
                        {copiedCode === brand.promo_code ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                {brand.website && (
                  <a href={brand.website} target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="w-full btn-gold" data-testid="shop-brand-btn">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Shop {brand.name}
                      <ExternalLink className="w-3 h-3 ml-2" />
                    </Button>
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Partner CTA */}
        <div className="mt-12 text-center p-8 bg-white rounded-sm shadow-sm">
          <ShoppingBag className="w-10 h-10 text-gold mx-auto mb-4" />
          <h3 className="font-display font-bold text-xl text-deep-green mb-2">Own a Minority Golfers Brand?</h3>
          <p className="text-muted-foreground font-body mb-4 max-w-xl mx-auto">
            List your brand on the Fairway Alliance Marketplace. Free basic listing — 
            give our members a discount code, and we'll send customers your way.
          </p>
          <Button variant="outline" className="border-fairway text-fairway hover:bg-fairway hover:text-white">
            Apply to Partner
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
